/*
 *
 * Copyright 2021-2024, White River Computing LLC. All rights reserved.
 * For licensing information, please contact Charlie Flock (cflock@whiterivercomputing.com or 888-802-6340)
 */
import './WrcRecordDetail.css';
import React from 'react';
import ReactDOM from "react-dom";
import { Button, Alert } from "@blueprintjs/core";
import { ButtonGroup, Card, Elevation } from "@blueprintjs/core";
import { Tab, Tabs } from "@blueprintjs/core";
import { Intent } from "@blueprintjs/core/lib/esm/index";
import { IconNames } from "@blueprintjs/icons";
import { Classes, Tooltip2 } from "@blueprintjs/popover2";
import { WrcCardHeader } from "./WrcCardHeader";
import { WrcForm } from "./WrcForm.js";
import { WrcGlobal } from "./WrcGlobal.js";
import { WrcServer } from "./WrcServer.js";
import WrcDetailContent from "./WrcDetailContent.js";
import { WrcEmailOpener } from "./WrcEmailOpener.js";

export class WrcRecordDetail extends React.Component {

    constructor(props) {
        super(props);
        let parentState = this.props.parentGetState()
        this.state = {
            is_loaded: false,
            show_list: (this.props.initially_enabled !== undefined ? this.props.initially_enabled : true),
            collapse_enabled: (this.props.enable_minimization !== undefined ? this.props.enable_minimization : false),
            current_tab: parentState['current_child_tab'] !== undefined ? parentState['current_child_tab'] : 'tab1',
            selected_grandchild: parentState['selected_grandchild'] !== undefined ? parentState['selected_grandchild'] : undefined,
            current_grandchild_tab: parentState['current_grandchild_tab'] !== undefined ? parentState['current_grandchild_tab'] : 'tab1',
        };
        this.handleTabChange = this.handleTabChange.bind(this);
        this.handleGrandChildTabChange = this.handleGrandChildTabChange.bind(this);
    }

    componentDidMount() {
        let children = this.unpack(this.props.children);
        if (Array.isArray(children)) {
            WrcServer.post('get-children',
                {
                    record_id: this.props.record_id, table_schema: this.props.table_schema, table_name: this.props.table_name,
                    children: children.filter((c) => c.table_name !== undefined)
                },
                (content) => this.getData(content)
            );
        } else {
            this.setState({
                is_loaded: true
            });
        }
    }

    componentWillUnmount() {
        this.props.parentSetState({
            current_child_tab: this.state.current_tab,
            selected_grandchild: this.state.selected_grandchild,
            current_grandchild_tab: this.state.current_grandchild_tab
        })
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevProps.record_id !== this.props.record_id) {
            let children = this.unpack(this.props.children);
            if (Array.isArray(children)) {
                WrcServer.post('get-children',
                    {
                        record_id: this.props.record_id, table_schema: this.props.table_schema, table_name: this.props.table_name,
                        children: children.filter((c) => c.table_name !== undefined)
                    },
                    (content) => this.getChildData(content)
                );
            }
        }
        if (prevState.current_tab !== this.state.current_tab) {
            this.setState({
                selected_grandchild: undefined,
                current_grandchild_tab: 'tab1',
            });
        }
        if (prevState.selected_grandchild !== this.state.selected_grandchild) {
            if (this.state.selected_grandchild !== null && this.state.selected_grandchild !== undefined) {
                document.getElementById('grandchild-' + this.props.component_index).scrollIntoView(false)
            } 
        }
    }

    // functions for changing tabs
    handleTabChange = (tabId) => {
        this.setState({
            current_tab: tabId
        });
    }

    handleGrandChildTabChange = (tabId) => {
        this.setState({
            current_grandchild_tab: tabId
        });
    }

    grandChildCallback = (newState) => {
        this.setState(newState)
    }

    selectGrandChild = (newGrandChild, readonly_if) => {
        let local_grandchildren_unpacked = [];
        let local_grandchildren_packed = [];
        this.props.children.forEach((child) => {
            if (Array.isArray(child.children)) {
                child.children.forEach((grand) => {
                    local_grandchildren_packed.push(grand);
                    if (grand.table_name !== undefined) {
                        local_grandchildren_unpacked.push(grand);
                    }
                    if (Array.isArray(grand.subtabs)) {
                        grand.subtabs.forEach((subtab) => {
                            local_grandchildren_unpacked.push(subtab);
                            if (Array.isArray(subtab.children)) {
                                subtab.children.forEach((sub_child) => {
                                    local_grandchildren_packed.push(sub_child);
                                });
                            }
                        });
                    }
                });
            }

            if (Array.isArray(child.subtabs)) {
                child.subtabs.forEach((subtab) => {
                    if (Array.isArray(subtab.children)) {
                        subtab.children.forEach((sub_child) => {
                            local_grandchildren_packed.push(sub_child);
                            local_grandchildren_unpacked.push(sub_child);
                        });
                    }
                });
            }

        });
        if (newGrandChild !== undefined && newGrandChild.record_id === this.props.record_id) {
            WrcServer.post('get-children',
                {
                    record_id: newGrandChild.parent_id, table_schema: newGrandChild.table_schema, table_name: newGrandChild.references,
                    children: local_grandchildren_unpacked.filter((gc) =>
                        gc.table_name !== undefined &&
                        gc.references !== undefined &&
                        gc.references == newGrandChild.references)
                },
                (grand_content) => this.getGrandChildData(grand_content, newGrandChild, local_grandchildren_packed, readonly_if)
            );
        }
    }

    // function for unpacking nested elements
    unpack(elem_condensed) {
        let elem_expanded;
        if (Array.isArray(elem_condensed)) {
            elem_expanded = [];
            for (let i = 0; i < elem_condensed.length; i++) {
                if (elem_condensed[i].table_name !== undefined) {
                    elem_expanded.push(elem_condensed[i]);
                }
                if (Array.isArray(elem_condensed[i].subtabs)) {
                    for (let j = 0; j < elem_condensed[i].subtabs.length; j++) {
                        if (elem_condensed[i].subtabs[j].table_name !== undefined) {
                            elem_expanded.push(elem_condensed[i].subtabs[j]);
                        }
                    }
                }
            }
        }

        return elem_expanded;
    }

    // function to check whether or not grandchildren exist
    grandChildExists() {
        let direct_grandchildren = false;
        let subtab_grandchildren = false;
        if (Array.isArray(this.props.children)) {
            var i;
            for (i = 0; i < this.props.children.length; i++) {
                if (Array.isArray(this.props.children[i].children)) {
                    direct_grandchildren = true;
                    break;
                }
                if (Array.isArray(this.props.children[i].subtabs)) {
                    for (let j = 0; j < this.props.children[i].subtabs.length; j++) {
                        if (Array.isArray(this.props.children[i].subtabs[j].children)) {
                            subtab_grandchildren = true;
                            break;
                        }
                    }
                }

            }
        }
        return (direct_grandchildren || subtab_grandchildren);
    }

    getData(content) {
        if (this.grandChildExists()) {
            let local_grandchildren_unpacked = [];
            let local_grandchildren_packed = [];
            this.props.children.forEach((child) => {
                if (Array.isArray(child.children)) {
                    child.children.forEach((grand) => {
                        local_grandchildren_packed.push(grand);
                        if (grand.table_name !== undefined) {
                            local_grandchildren_unpacked.push(grand);
                        }
                        if (Array.isArray(grand.subtabs)) {
                            grand.subtabs.forEach((subtab) => {
                                local_grandchildren_unpacked.push(subtab);
                                if (Array.isArray(subtab.children)) {
                                    subtab.children.forEach((sub_child) => {
                                        local_grandchildren_packed.push(sub_child);
                                    });
                                }
                            });
                        }
                    });
                }

                if (Array.isArray(child.subtabs)) {
                    child.subtabs.forEach((subtab) => {
                        if (Array.isArray(subtab.children)) {
                            subtab.children.forEach((sub_child) => {
                                local_grandchildren_packed.push(sub_child);
                                local_grandchildren_unpacked.push(sub_child);
                            });
                        }
                    });
                }

            });

            // occurs when a refresh happens and a grandchild was selected
            if (this.state.selected_grandchild && this.state.selected_grandchild.record_id === this.props.record_id) {
                WrcServer.post('get-children',
                    {
                        record_id: this.state.selected_grandchild.parent_id, table_schema: this.state.selected_grandchild.table_schema, table_name: this.state.selected_grandchild.references,
                        children: local_grandchildren_unpacked.filter((gc) =>
                            gc.table_name !== undefined &&
                            gc.references !== undefined &&
                            gc.references == this.state.selected_grandchild.references)
                    },
                    (grand_content) => this.setState({
                        is_loaded: true,
                        grandchild_list_data: grand_content,
                        child_list_data: content,
                        grandchildren: local_grandchildren_packed
                    })
                );
                // document.getElementById('grandchild-' + this.props.component_index).scrollIntoView(false)
            } else {
                this.setState({
                    is_loaded: true,
                    child_list_data: content,
                    grandchildren: local_grandchildren_packed
                });
            }
        } else {
            this.setState({
                child_list_data: content,
                is_loaded: true
            });
        }
    }

    getChildData(content) {
        this.setState({
            is_loaded: true,
            selected_grandchild: undefined,
            current_grandchild_tab: 'tab1',
            current_tab: 'tab1',
            child_list_data: content
        });
    }

    getGrandChildData(grand_content, newGrandChild, grandchildren, readonly_if) {
        this.setState({
            is_loaded: true,
            grandchild_readonly: readonly_if,
            grandchild_list_data: grand_content,
            selected_grandchild: newGrandChild,
            grandchildren: grandchildren
        });
    }

    saveDocument = (table_name, entry_date) => () => {
        WrcServer.post('save-form',
            { table_schema: this.props.table_schema, table_name: table_name, data: { "patient_id": this.props.record_id, "entry_date": entry_date } },
            (content) => this.afterSave(content)
        );
    }

    openDocument = (doc) => (e) => {
        var today = WrcGlobal.get_db_today();
        var display = window.open("", "_blank");
        var column_name;
        var content = doc.content.replace(new RegExp("~today~", "g"), today);

        for (column_name in this.props.list_data[this.props.selected_item]) {
            content = content.replace(new RegExp("~" + column_name + "~", "g"), this.props.list_data[this.props.selected_item][column_name]);
        }
        for (let user_field in this.props.user) {
            content = content.replace(new RegExp("~user." + user_field + "~", "g"), this.props.user[user_field]);
        }
        display.document.write('<div style="height: 11.0in; width: 8.5in; margin: 0.25in;">' + content + '</div>');

        let alertBox = (
            <Alert
                cancelButtonText="No"
                confirmButtonText="Yes"
                isOpen={true}
                intent={Intent.SUCCESS}
                icon={IconNames.TICK}
                onCancel={() => this.cancelAlert()}
                onConfirm={() => this.saveDocument(doc.table_name, today)}>
                <p>Do you want to record that you generated this document?</p>
            </Alert>
        );
        ReactDOM.render(alertBox, document.querySelector('#alertdiv'));
    }

    afterDelete(content) {
        this.setState({ record_id: undefined });
        this.props.refresh();
    }

    afterSave = (content) => {
        ReactDOM.unmountComponentAtNode(document.querySelector('#alertdiv'));
        this.props.refresh();
    }

    cancelAlert() {
        ReactDOM.unmountComponentAtNode(document.querySelector('#alertdiv'));
    }

    confirmDelete() {
        ReactDOM.unmountComponentAtNode(document.querySelector('#alertdiv'));
        WrcServer.post('delete-record',
            { table_schema: this.props.table_schema, table_name: this.props.table_name, id: this.props.record_id },
            (content) => this.afterDelete(content)
        );
    }

    clickDelete = () => {
        let deleteAlert = (
            <Alert
                cancelButtonText="Cancel"
                confirmButtonText="Delete"
                isOpen={true}
                intent={Intent.DANGER}
                icon={IconNames.WARNING_SIGN}
                onCancel={() => this.cancelAlert()}
                onConfirm={() => this.confirmDelete()}>
                <p>Are you sure you wish to delete this record?</p>
            </Alert>
        );
        ReactDOM.render(deleteAlert, document.querySelector('#alertdiv'));
    }

    clickEdit() {
        ReactDOM.render(
            <WrcForm
                form_id={this.props.form_id}
                refresh={this.props.refresh}
                parentCallback={this.props.parentCallback}
                record_id={this.props.record_id}
                readonly={this.props.readonly ? this.props.readonly : this.props.summary_readonly_if_check ? this.props.summary_readonly_if_check : false}
                component_index={this.props.component_index} />,
            document.querySelector('#form'));
    }

    clickNewChild = (child) => (e) => {
        var default_fields = undefined;
        if (child.default_fields !== undefined) {
            default_fields = child.default_fields.map(function (field) {
                return ({ name: field.name, value: this.props.list_data[this.props.selected_item][field.source] });
            });
        }
        ReactDOM.render(
            <WrcForm
                form_id={child.form_id}
                refresh={this.props.refresh}
                default_fields={default_fields}
                fkey={this.props.record_id}
                component_index={this.props.component_index} />,
            document.querySelector('#form'));
    }

    render() {
        // variables that need to be defined for final return JSX
        let rd_full_class = this.props.app_layout.ld_child_grandchild_layout === 'vertical' ? 'app-record-detail-v' : 'app-record-detail-h';
        let rd_children_class = this.props.app_layout.ld_child_grandchild_layout === 'vertical' ? 'app-record-detail-children-v' : 'app-record-detail-children-h';
        let rd_grandchildren_class = this.props.app_layout.ld_child_grandchild_layout === 'vertical' ? 'app-record-detail-grandchildren-v' : 'app-record-detail-grandchildren-h';
        let rd_card_alignment = this.props.component_alignment === 'center' ? 'center' : 'left';
        rd_full_class += " ";
        rd_full_class += rd_card_alignment;

        let id = '';
        let title = '';
        let detail_fields = [];
        let detail_buttons = '';
        let tabs = [];
        let grandchild_display = (<div></div>);

        if (this.state.is_loaded) {
            let summary_buttons = (<div></div>);
            let summary_edit_button = null;
            let summary_delete_button = null;
            if (this.props.hide_edit !== true) {
                summary_edit_button = (
                    <Button icon={IconNames.EDIT}
                        text={this.props.readonly || this.props.summary_readonly_if_check ? 'View' : 'Edit'}
                        minimal={true}
                        onClick={() => this.clickEdit()} />
                );
            }
            if (this.props.hide_delete !== true && this.props.readonly !== true && this.props.summary_readonly_if_check !== true) { 
                summary_delete_button = (
                    <Button icon={IconNames.TRASH}
                        text={'Delete'}
                        minimal={true}
                        onClick={() => this.clickDelete()} />
                );
            }
            if (summary_edit_button || summary_delete_button) {
                summary_buttons = (
                    <ButtonGroup minimal={true} large={false} style={{ float: 'right', marginTop: -4 }}>
                        {summary_edit_button}
                        {summary_delete_button}
                    </ButtonGroup>
                );
            }
            title = (
                <span>
                    Summary
                    {summary_buttons}
                </span>
            );

            id = this.props.local_id ? this.props.list_data[this.props.selected_item][this.props.local_id] : this.props.record_id;
            detail_fields = this.props.columns.map(function (column) {
                if (column.show_if !== undefined) {
                    for (let i = 0; i < column.show_if.length; i++) {
                        let match_not_found = column.show_if[i].values.every(function (value) {
                            return (value !== this.props.list_data[this.props.selected_item][column.show_if[i].column_name]);
                        });
                        if (match_not_found) {
                            return '';
                        }
                    }
                }
                if (column.show_in_summary === false) {
                    return ''
                } else {
                    let display_value = this.props.list_data[this.props.selected_item][column.field];
                    if (display_value && column.element_type === 'datestamp') {
                        display_value = (
                            <Tooltip2 minimal={true} className={Classes.TOOLTIP2_INDICATOR} content={display_value} intent={Intent.PRIMARY} placement="right">
                                {display_value.substring(0, 10)}
                            </Tooltip2>
                        );
                    }
                    if (display_value === true) display_value = 'Yes';
                    if (display_value === false) display_value = 'No';
                    return (
                        <tr>
                            <td className='wd-table-label'>{column.header}</td>
                            <td className='wd-table-data'>{display_value}</td>
                        </tr>
                    );
                };
            }.bind(this));

            if (this.props.detail_buttons !== undefined && this.props.summary_readonly_if_check !== true) {
                detail_buttons = this.props.detail_buttons.map(function (button) {
                    if (button.show_if !== undefined) {
                        for (let i = 0; i < button.show_if.length; i++) {
                            let match_not_found = button.show_if[i].values.every(function (value) {
                                return (value !== this.props.list_data[this.props.selected_item][button.show_if[i].column_name]);
                            });
                            if (match_not_found) {
                                return '';
                            }
                        }
                    }

                    if (button.form_id !== undefined) {
                        return (
                            <div className="wd-record">
                                <Button
                                    intent={Intent.PRIMARY}
                                    text={button.label}
                                    style={{ marginTop: '1em' }}
                                    fill={false}
                                    onClick={(button) => this.clickNewChild(button)} />
                            </div>
                        );
                    }
                    else if (button.document !== undefined) {
                        return (
                            <div className="wd-record">
                                <Button
                                    intent={Intent.PRIMARY}
                                    text={button.label}
                                    style={{ marginTop: '1em' }}
                                    fill={false}
                                    onClick={(button) => this.openDocument(button.document)} />
                            </div>
                        );
                    } else if (button.type !== undefined && button.type === 'email') {
                        return (
                            <WrcEmailOpener
                                refresh={this.props.refresh}

                                component_index={this.props.component_index}
                                table_schema={button.table_schema}
                                table_name={button.table_name}
                                recipients={button.recipients}
                                date_component={button.date_component}
                                content_query={button.content_query}
                                template={button.template}
                                non_ideal_state_message={button.non_ideal_state_message}
                                default_subject={button.default_subject}
                                user={this.props.user}
                                specified_recipient_name={button.specified_recipient_name}
                                specified_recipient_email={button.specified_recipient_email}
                                recipient_type={button.recipient_type}
                                title={button.title}
                                button_text={button.button_text}
                                record={this.props.list_data[this.props.selected_item]}
                            />
                        );
                    }
                }.bind(this));
            }

            if (Array.isArray(this.props.children)) {
                tabs = this.props.children.map(function (child, index) {
                    return (
                        <Tab title={child.title} id={'tab' + (index + 2)} panel={
                            <div>
                                <WrcDetailContent
                                    program_role={this.props.program_role}
                                    readonly_if={child.readonly_if}

                                    height={560}
                                    descendants={child.children}
                                    component_index={this.props.component_index}
                                    refresh={this.props.refresh}
                                    table_schema={this.props.table_schema}
                                    table_name={child.table_name}
                                    references={this.props.table_name}
                                    selected_element={child}
                                    elements={this.props.children}
                                    display_type={child.display}
                                    page_length={this.props.page_length}
                                    class_name={this.props.class_name}
                                    index={index}
                                    subtraction={2}
                                    subtabs={child.subtabs}
                                    level={'child'}
                                    readonly={this.props.readonly}
                                    selected_item={this.props.selected_item}
                                    selectGrandChild={this.selectGrandChild}
                                    grandChildCallback={this.grandChildCallback}
                                    selected_grandchild={this.state.selected_grandchild}
                                    parentSetState={this.props.parentSetState}
                                    parentGetState={this.props.parentGetState}
                                    list_data={this.props.list_data}
                                    child_list_data={this.state.child_list_data}
                                    grandchild_list_data={this.state.grandchild_list_data}
                                    current_tab={this.state.current_tab}
                                    current_grandchild_tab={this.state.current_grandchild_tab}
                                    record_id={this.props.record_id}
                                />
                            </div>
                        } />
                    );
                }.bind(this));
            }
            if (this.state.grandchild_list_data !== undefined && this.state.selected_grandchild !== undefined) {
                let grandchild_tabs = [];
                let entity = this.state.selected_grandchild.references[0].toUpperCase() + this.state.selected_grandchild.references.slice(1);

                let children = this.unpack(this.props.children);
                let target_child = children.find((child) => {
                    return child.table_name === this.state.selected_grandchild.references;
                });
                let entity_title = target_child.title;
                let direct_grandchildren = this.state.grandchildren.filter((gc) =>
                    gc.table_name !== undefined &&
                    gc.references !== undefined &&
                    gc.references == target_child.table_name)
                grandchild_tabs = direct_grandchildren.map(function (grandchild, index) {
                    return (
                        <Tab title={grandchild.title} id={'tab' + (index + 1)} panel={
                            <div>
                                <WrcDetailContent
                                    program_role={this.props.program_role}
                                    readonly_if={this.state.grandchild_readonly}

                                    height={560}
                                    descendants={undefined}
                                    component_index={this.props.component_index}
                                    refresh={this.props.refresh}
                                    table_schema={this.props.table_schema}
                                    table_name={grandchild.table_name}
                                    references={entity}
                                    selected_element={grandchild}
                                    elements={direct_grandchildren}
                                    display_type={grandchild.display}
                                    page_length={this.props.page_length}
                                    class_name={this.props.class_name}
                                    index={index}
                                    subtraction={1}
                                    fkey={this.state.selected_grandchild ? this.state.selected_grandchild.parent_id : undefined}
                                    subtabs={grandchild.subtabs}
                                    level={'grandchild'}
                                    readonly={this.props.readonly}
                                    parentCallback={this.props.parentCallback}
                                    selectGrandChild={this.selectGrandChild}
                                    grandChildCallback={this.grandChildCallback}
                                    selected_grandchild={this.state.selected_grandchild}
                                    selected_item={this.props.selected_item}
                                    parentSetState={this.props.parentSetState}
                                    parentGetState={this.props.parentGetState}
                                    list_data={this.props.list_data}
                                    child_list_data={this.state.child_list_data}
                                    grandchild_list_data={this.state.grandchild_list_data}
                                    current_tab={this.state.current_tab}
                                    current_grandchild_tab={this.state.current_grandchild_tab}
                                    record_id={this.props.record_id}
                                />
                            </div>
                        } />
                    );
                }.bind(this));

                grandchild_display = (
                    <Card interactive={false} elevation={Elevation.TWO} style={{ height: '560px', minWidth: '800px' }}>
                        <WrcCardHeader small={true} title={this.props.entity + ' ID ' + id} />
                        <WrcCardHeader small={true} title={entity_title + ' # ' + this.state.selected_grandchild.instance_number} />
                        <Tabs
                            large={false}
                            renderActiveTabPanelOnly={true}
                            onChange={(tab) => this.handleGrandChildTabChange(tab)}
                            selectedTabId={this.state.current_grandchild_tab}>
                            {grandchild_tabs}
                        </Tabs>
                    </Card>
                );
            }
        }

        return ((this.state.is_loaded) ?
            (<div className={rd_full_class}>
                <div className={rd_children_class}>
                    <Card interactive={false} elevation={Elevation.TWO} style={{ minHeight: '560px', minWidth: '1080px' }}>
                        <WrcCardHeader small={true} title={this.props.entity + ' ID ' + id} />
                        <Tabs
                            large={false}
                            renderActiveTabPanelOnly={true}
                            onChange={(tab) => this.handleTabChange(tab)}
                            selectedTabId={this.state.current_tab}>
                            <Tab title='Summary' id={'tab1'} panel={
                                <Card interactive={false} elevation={Elevation.ZERO} className='wd-card-m-top'>
                                    <span>{title}</span>
                                    <table className={'bp3-html-table bp3-small bp3-html-table-striped'} style={{ width: '100%', marginTop: 12 }}>
                                        <tbody>
                                            {detail_fields}
                                        </tbody>
                                    </table>
                                    {detail_buttons}
                                </Card>
                            } />
                            {tabs}
                        </Tabs>
                    </Card>
                </div>
                <div className={rd_grandchildren_class} id={'grandchild-' + this.props.component_index}>
                    {grandchild_display}
                </div>
            </div>) :
            (<div>
                <Card style={{ height: '560px' }}><h3>Loading...</h3></Card>
            </div>)
        );
    }
}

export default WrcRecordDetail;
